import { useState } from 'react'

/**
 * A v4 UUID THIS BROWSER CAN ALWAYS PRODUCE.
 *
 * `crypto.randomUUID()` is restricted to secure contexts. The operator reaches
 * the app on `http://127.0.0.1:27070`, which browsers treat as potentially
 * trustworthy, so it is available there — but on any other plain-HTTP origin it
 * is `undefined`, and calling it throws while rendering the upload form, taking
 * the whole patient overview down with it. A live browser on `http://web/`
 * found exactly that.
 *
 * `crypto.getRandomValues` carries no such restriction, so the fallback is real
 * randomness rather than a weaker source.
 */
export function newBrowserUuid(): string {
  const webCrypto = globalThis.crypto
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }

  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    webCrypto.getRandomValues(bytes)
    // RFC 4122 version 4, variant 10xx.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40
    bytes[8] = (bytes[8]! & 0x3f) | 0x80
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  // An idempotency key needs uniqueness, not cryptographic secrecy. This last
  // fallback keeps a missing browser API from becoming a render-time product
  // outage while still separating command attempts.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

/**
 * ONE WRITE ATTEMPT'S IDENTITY — the same UUID, named for the claim a caller is
 * making. A call site asking for an idempotency key is saying something about
 * what a retry means, not about randomness, and reading it as `newBrowserUuid`
 * at the request would hide which requests are safe to retry.
 */
export function newIdempotencyKey(): string {
  return newBrowserUuid()
}

/**
 * A KEY PER INTENT: the attempt id for ONE payload, retired when that payload
 * changes or when the write it names lands.
 *
 * An idempotency key promises the server that a repeat of THIS request is the
 * same request, and a key minted once per mounted form breaks the promise in
 * both directions. A submission that failed, was corrected and sent again asked
 * the server to replay its answer to the payload the reader had just rejected;
 * a second deliberate create after a successful one replayed the first. Both
 * were silent — the server answers `replayed`, and the form reports success.
 *
 * `signature` is whatever the caller puts on the wire, so editing any of it is
 * a new intent. A signature that changes when the payload did not merely mints
 * a key nobody used, which is free; a signature that misses an edited field is
 * the defect. `retire` is what a success calls, because an accepted payload is
 * finished even when every field still reads the same.
 */
export function useIdempotentAttempt(
  signature: string,
  /** How a key is minted, for a route that reads its keys in its own spelling. */
  mint: () => string = newIdempotencyKey,
): { key: string; retire: () => void } {
  const [attempt, setAttempt] = useState(() => ({ signature, key: mint() }))
  if (attempt.signature !== signature) {
    setAttempt({ signature, key: mint() })
  }
  return {
    key: attempt.key,
    retire: () => setAttempt((current) => ({ ...current, key: mint() })),
  }
}
